// src/views/auth/Login.js
//
// The AiViz document's cover, as a sign-in page: a white panel carrying the
// logo and the form, beside a solid teal band carrying the name and what the
// system does. Flat, printed, no glass and no animated backdrop.
//
// The form logic is unchanged from the version this replaced.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  InputAdornment,
  Paper,
} from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PaymentsIcon from '@mui/icons-material/Payments';
import BoltIcon from '@mui/icons-material/Bolt';

import { login } from '../../store/auth/authSlice';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

const FEATURES = [
  { icon: FolderOpenIcon, label: 'Track every project end to end' },
  { icon: PaymentsIcon, label: 'Stay on top of payments and expenses' },
  { icon: BoltIcon, label: 'Provision AITIME licences in minutes' },
];

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoggedIn, isLoading, error } = useSelector((state) => state.auth);
  const [noRoleError, setNoRoleError] = useState(null);
  const reduceMotion = useReducedMotion();

  React.useEffect(() => {
    if (isLoggedIn) {
      navigate('/dashboard');
    }
  }, [isLoggedIn, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  const onSubmit = (values) => {
    setNoRoleError(null);

    dispatch(login(values))
      .unwrap()
      .then(() => {
        navigate('/dashboard');
      })
      .catch((loginError) => {
        if (typeof loginError === 'string' && loginError.includes('not been assigned a role')) {
          setNoRoleError(loginError);
        }
      });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: { xs: 2, sm: 3 },
      }}
    >
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        style={{ width: '100%', maxWidth: 940 }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            overflow: 'hidden',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Box sx={{ flex: 1, p: { xs: 3, sm: 5 } }}>
            <Box
              component="img"
              src="/assets/logo.png"
              alt="AiViz"
              sx={{ height: 38, mb: 4, display: 'block' }}
            />

            <Typography variant="h2" component="h1">
              Sign in
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
              Project Management System
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {error && !noRoleError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {noRoleError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Account not activated</AlertTitle>
                  <Typography variant="body2">{noRoleError}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Your account exists but has not been assigned a role yet. Please
                    contact an administrator to activate it.
                  </Typography>
                </Alert>
              )}

              <TextField
                fullWidth
                size="medium"
                label="Username"
                autoComplete="username"
                error={!!errors.username}
                helperText={errors.username?.message}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                {...register('username')}
              />

              <TextField
                fullWidth
                size="medium"
                label="Password"
                type="password"
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                {...register('password')}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading}
                sx={{ py: 1.1 }}
              >
                {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Sign in'}
              </Button>

              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Button variant="text" size="small" onClick={() => navigate('/register')}>
                  Create an account
                </Button>
                <Button variant="text" size="small" color="inherit">
                  Forgot password?
                </Button>
              </Box>
            </form>
          </Box>

          {/* The document's teal band. Hidden on phones, where the form needs the width. */}
          <Box
            sx={{
              width: '42%',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 3,
              p: 5,
              bgcolor: 'primary.dark',
              color: 'common.white',
            }}
          >
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.75 }}>
                Aiviz Electronics Trading L.L.C
              </Typography>
              <Typography variant="h3" sx={{ mt: 0.5 }}>
                Project Management System
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              {FEATURES.map(({ icon: Icon, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Icon fontSize="small" sx={{ opacity: 0.85 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              aiviz.me &middot; aitime.app
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default Login;
